import React, { useState, useEffect } from 'react';
import { usePackagingOperationsContext } from '../context/PackagingOperationsContext';
import { useMsal } from '@azure/msal-react';
import { SP_MSAL_SCOPE, buildSharePointListItemsUrl } from '../../../../constants/sharepoint';

const SetupComponent: React.FC = () => {
    const { state, dispatch } = usePackagingOperationsContext();
    const { instance, accounts } = useMsal();
    const [qaOptions, setQaOptions] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchUsers = async () => {
            if (accounts.length === 0) return;
            setLoading(true);
            try {
                const tokenResponse = await instance.acquireTokenSilent({
                    scopes: [SP_MSAL_SCOPE],
                    account: accounts[0]
                });
                
                const url = buildSharePointListItemsUrl('EmployeeList', '$select=Title,EmployeeName/Title&$expand=EmployeeName');
                const response = await fetch(url, {
                    headers: {
                        'Authorization': `Bearer ${tokenResponse.accessToken}`,
                        'Accept': 'application/json;odata=verbose'
                    }
                });
                
                if (response.ok) {
                    const data = await response.json();
                    const employees = data.d.results.map((r: any) => r.EmployeeName?.Title || r.Title).filter(Boolean);
                    const uniqueNames = Array.from(new Set(employees)) as string[];
                    uniqueNames.sort();
                    setQaOptions(uniqueNames);
                    
                    if (!state.sessionData.qaExecutive) {
                        if (accounts[0].name && uniqueNames.includes(accounts[0].name)) {
                            dispatch({ type: 'SET_SESSION_DATA', payload: { qaExecutive: accounts[0].name } });
                        } else if (uniqueNames.length > 0) {
                            dispatch({ type: 'SET_SESSION_DATA', payload: { qaExecutive: uniqueNames[0] } });
                        }
                    }
                }
            } catch (err) {
                console.error("Error fetching QA users from EmployeeList", err);
            } finally {
                setLoading(false);
            }
        };
        fetchUsers();
    }, [instance, accounts]);

    const handleStartTour = () => {
        dispatch({
            type: 'START_TOUR',
            payload: { ...state.sessionData, tourId: `TOUR-${Math.floor(Math.random() * 10000)}` }
        });
    };

    return (
        <div style={{ backgroundColor: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '1.5rem' }}>
            <div style={{ borderBottom: '1px solid #cbd5e1', padding: '15px 20px', backgroundColor: '#f8fafc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>Checklist Setup Form</h4>
            </div>
            
            <div style={{ padding: '20px' }}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label style={{ display: 'block', fontSize: '14px', marginBottom: '8px', color: '#333' }}>Date</label>
                        <input type="date" 
                            style={{ display: 'block', width: '100%', padding: '0.375rem 0.75rem', fontSize: '1rem', fontWeight: 400, lineHeight: 1.5, color: '#212529', backgroundColor: '#e9ecef', backgroundClip: 'padding-box', border: '1px solid #ced4da', borderRadius: '0.25rem', opacity: 1 }}
                            value={state.sessionData.date}
                            disabled 
                            readOnly
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '14px', marginBottom: '8px', color: '#333' }}>Time</label>
                        <input type="time" 
                            style={{ display: 'block', width: '100%', padding: '0.375rem 0.75rem', fontSize: '1rem', fontWeight: 400, lineHeight: 1.5, color: '#212529', backgroundColor: '#e9ecef', backgroundClip: 'padding-box', border: '1px solid #ced4da', borderRadius: '0.25rem', opacity: 1 }}
                            value={state.sessionData.time}
                            disabled 
                            readOnly
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '14px', marginBottom: '8px', color: '#333' }}>Site</label>
                        <select 
                            style={{ display: 'block', width: '100%', padding: '0.375rem 2.25rem 0.375rem 0.75rem', fontSize: '1rem', fontWeight: 400, lineHeight: 1.5, color: '#212529', backgroundColor: '#fff', border: '1px solid #ced4da', borderRadius: '0.25rem' }}
                            value={state.sessionData.site}
                            onChange={(e) => dispatch({ type: 'SET_SESSION_DATA', payload: { site: e.target.value } })}
                        >
                            <option value="Rajpura">Rajpura</option>
                            <option value="Phillaur">Phillaur</option>
                            <option value="Tahliwal">Tahliwal</option>
                            <option value="Indore">Indore</option>
                        </select>
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '14px', marginBottom: '8px', color: '#333' }}>Line No</label>
                        <select 
                            style={{ display: 'block', width: '100%', padding: '0.375rem 2.25rem 0.375rem 0.75rem', fontSize: '1rem', fontWeight: 400, lineHeight: 1.5, color: '#212529', backgroundColor: '#fff', border: '1px solid #ced4da', borderRadius: '0.25rem' }}
                            value={state.sessionData.lineNo}
                            onChange={(e) => dispatch({ type: 'SET_SESSION_DATA', payload: { lineNo: e.target.value } })}
                        >
                            <option value="Line 1">Line 1</option>
                            <option value="Line 2">Line 2</option>
                            <option value="Line 3">Line 3</option>
                            <option value="Line 4">Line 4</option>
                            <option value="Line 5">Line 5</option>
                            <option value="Line 6">Line 6</option>
                            <option value="Line 7">Line 7</option>
                            <option value="Line 8">Line 8</option>
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div>
                        <label style={{ display: 'block', fontSize: '14px', marginBottom: '8px', color: '#333' }}>Assign QA Executive</label>
                        <select 
                            style={{ display: 'block', width: '100%', padding: '0.375rem 2.25rem 0.375rem 0.75rem', fontSize: '1rem', fontWeight: 400, lineHeight: 1.5, color: '#212529', backgroundColor: '#fff', border: '1px solid #ced4da', borderRadius: '0.25rem' }}
                            value={state.sessionData.qaExecutive}
                            onChange={(e) => dispatch({ type: 'SET_SESSION_DATA', payload: { qaExecutive: e.target.value } })}
                            disabled={loading}
                        >
                            {loading && <option value="">Loading...</option>}
                            {!loading && qaOptions.length === 0 && <option value="">No options</option>}
                            {qaOptions.map(qa => <option key={qa} value={qa}>{qa}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '14px', marginBottom: '8px', color: '#333' }}>Shift Executive Production</label>
                        <input type="text" 
                            style={{ display: 'block', width: '100%', padding: '0.375rem 0.75rem', fontSize: '1rem', fontWeight: 400, lineHeight: 1.5, color: '#212529', backgroundColor: '#fff', backgroundClip: 'padding-box', border: '1px solid #ced4da', borderRadius: '0.25rem' }}
                            placeholder="Production Executive Name"
                            value={state.sessionData.productionIncharge}
                            onChange={(e) => dispatch({ type: 'SET_SESSION_DATA', payload: { productionIncharge: e.target.value } })}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '14px', marginBottom: '8px', color: '#333' }}>Shift</label>
                        <select 
                            style={{ display: 'block', width: '100%', padding: '0.375rem 2.25rem 0.375rem 0.75rem', fontSize: '1rem', fontWeight: 400, lineHeight: 1.5, color: '#212529', backgroundColor: '#fff', border: '1px solid #ced4da', borderRadius: '0.25rem' }}
                            value={state.sessionData.shift}
                            onChange={(e) => dispatch({ type: 'SET_SESSION_DATA', payload: { shift: e.target.value } })}
                        >
                            <option value="Shift 1">Shift 1</option>
                            <option value="Shift 2">Shift 2</option>
                            <option value="Shift 3">Shift 3</option>
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                        <label style={{ display: 'block', fontSize: '14px', marginBottom: '8px', fontWeight: 'bold', color: '#2563eb' }}>Select Sub-Checklist parameter</label>
                        <select 
                            style={{ display: 'block', width: '100%', padding: '0.375rem 2.25rem 0.375rem 0.75rem', fontSize: '1rem', fontWeight: 600, lineHeight: 1.5, color: '#212529', backgroundColor: '#fff', border: '2px solid #3b82f6', borderRadius: '0.25rem' }}
                            value={state.sessionData.pkgopsType || ''}
                            onChange={(e) => dispatch({ type: 'SET_SESSION_DATA', payload: { pkgopsType: e.target.value as any } })}
                        >
                            <option value="" disabled>-- Select Parameter --</option>
                            <option value="Temperatures & Humidity">Temperatures & Humidity</option>
                            <option value="Code Verification">Code Verification</option>
                            <option value="PAPA">PAPA</option>
                            <option value="PQI">PQI</option>
                            <option value="Seal Integrity">Seal Integrity</option>
                            <option value="Cream Percentage">Cream Percentage</option>
                            <option value="Quality Wall Records">Quality Wall Records</option>
                        </select>
                    </div>
                </div>

                <div className="mt-5" style={{ textAlign: 'right' }}>
                    <button 
                        type="button" 
                        onClick={handleStartTour}
                        disabled={!state.sessionData.pkgopsType}
                        style={{ backgroundColor: '#2563eb', color: '#ffffff', padding: '10px 20px', borderRadius: '6px', border: 0, fontWeight: 700, cursor: state.sessionData.pkgopsType ? 'pointer' : 'not-allowed', transition: 'all 0.2s', opacity: state.sessionData.pkgopsType ? 1 : 0.6 }}
                    >
                        Start Quality Tour
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SetupComponent;
